import { type FC, useContext } from "react";
import { MobileModPrice, Wrapper } from "components/Common/LineItemGrid/Modification/modification.styles";
import { RequiredSpecificationLabel } from "../../../NewOrders/AddLineItem/constants.ts";
import { AddLineItemRequiredDimensions, ModRequiredContent } from "../../../NewOrders/AddLineItem/AddLineItemStyles.ts";
import { models } from "types/api/viewModels.ts";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import { formatPriceWithPrefix } from "utils/string.ts";

export interface ModificationProps {
	description?: string | null;
	listPrice?: number | null;
	netPrice?: number | null;
	isAcknowledgementEligible?: boolean;
	lineNumber: number | null | undefined;
	showLineNumber?: boolean;
	title?: string | null;
	dimensionDescription?: string | null;
	dimensionValue?: number;
	isUnrestrictedDimension?: boolean;
	values?: models["PendingValueViewModel"][] | null | undefined;
	requiredCategory?: models["PendingRequiredCategoryValueViewModel"] | null;
	specialModification?: models["SpecialModificationViewModel"] | null;
	showSpecialModPrice?: boolean;
}

const Modification: FC<ModificationProps> = (props) => {
	const { configurationsData } = useContext(ConfigurationsContext);
	const {
		description,
		listPrice,
		netPrice,
		isAcknowledgementEligible = false,
		requiredCategory,
		lineNumber,
		showLineNumber = true,
		title,
		dimensionDescription,
		dimensionValue,
		isUnrestrictedDimension,
		values,
		specialModification,
		showSpecialModPrice = true
	} = props;
	const hasLineNumber = showLineNumber && lineNumber !== null && typeof lineNumber !== "undefined";

	const getSpecialModPriceValue = () => {
		if (specialModification?.price && specialModification?.priceType === "Fixed") {
			var formattedPrice = formatPriceWithPrefix(specialModification?.price);
			return specialModification?.price > 0 ? `+${formattedPrice}` : formattedPrice;
		} else if (specialModification?.priceType === "Fixed") return formatPriceWithPrefix(specialModification?.price);

		if (specialModification?.price) {
			const formattedPricePercent = specialModification?.price * 100;
			return specialModification?.price > 0 ? `+${formattedPricePercent}%` : `${formattedPricePercent}%`;
		}
		return "";
	};

	return (
		<Wrapper
			isSpecialModification={!!specialModification}
			hasLineNumber={hasLineNumber}
		>
			{!specialModification ? (
				<div>
					{hasLineNumber && <div data-testid="line-item-grid-modifications-line-number">{lineNumber}</div>}
					<div>
						{title && (
							<div data-testid="line-item-grid-modifications-line-title">
								{title && (
									<>
										{title} - {description}
									</>
								)}
								{configurationsData?.featureFlags?.acknowledgementsEnabled &&
									isAcknowledgementEligible && (
										<MobileModPrice>
											{formatPriceWithPrefix(netPrice ?? listPrice, true)}
										</MobileModPrice>
									)}
							</div>
						)}
						{values?.map((value) => {
							if (value.value === null || typeof value.value === "undefined") {
								return null;
							}

							return (
								<div key={`${value.description ?? "value"}-${value.value}`}>
									<div data-testid="line-item-grid-modifications-line-description">
										{value.description} <span>{`${value.value}"`}</span>
									</div>
								</div>
							);
						})}

						{dimensionDescription && (
							<AddLineItemRequiredDimensions>
								<div data-testid="line-item-grid-modifications-line-required-dimension-label">
									{RequiredSpecificationLabel}
									<span data-testid="line-item-grid-modifications-line-required-dimension-description">
										{dimensionDescription}
									</span>
									<span data-testid="line-item-grid-modifications-line-required-dimension-value">
										{isUnrestrictedDimension ? `${dimensionValue}` : `${dimensionValue}"`}
									</span>
								</div>
							</AddLineItemRequiredDimensions>
						)}
						{requiredCategory && requiredCategory?.sku && (
							<ModRequiredContent>
								<div data-testid="line-item-grid-modifications-line-hinge-pattern-label">
									{RequiredSpecificationLabel}
									{requiredCategory?.categoryName}
									<span data-testid="line-item-grid-modifications-required-category-sku">
										{requiredCategory.sku}
									</span>
								</div>
							</ModRequiredContent>
						)}
					</div>
				</div>
			) : (
				<div>
					<div>
						<div>
							{title} - {description}
						</div>
						<span>{specialModification?.notes}</span>
					</div>
					{specialModification && showSpecialModPrice && <div>{getSpecialModPriceValue()}</div>}
				</div>
			)}

			{configurationsData?.featureFlags?.acknowledgementsEnabled && isAcknowledgementEligible && (
				<div>
					<>{formatPriceWithPrefix(netPrice ?? listPrice, true)}</>
				</div>
			)}
		</Wrapper>
	);
};

export default Modification;
